import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  Close as CloseIcon,
  KeyboardArrowUp as SendIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { generateResponse, initializeGemini } from '../services/geminiService';
import MeetingScheduler from './MeetingScheduler';

function ChatButton({ IconComponent = AiIcon, sx = {} }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { text: 'Hello! I\'m your AI assistant. How can I help you with your properties today?', sender: 'ai', timestamp: new Date() }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);
  
  const { properties } = useSelector((state) => state.property);
  
  useEffect(() => {
    const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
    if (GEMINI_API_KEY) {
      initializeGemini(GEMINI_API_KEY);
    }
  }, []);

  const handleSend = async () => {
    if (message.trim()) {
      const userMessage = { text: message, sender: 'user', timestamp: new Date() };
      setChatMessages(prev => [...prev, userMessage]);
      setMessage('');
      setIsLoading(true);

      try {
        if (!properties || properties.length === 0) {
          setChatMessages(prev => [...prev, {
            text: 'I don\'t have access to any property information at the moment. Please try again later when properties are loaded.',
            sender: 'ai',
            timestamp: new Date()
          }]);
          return;
        }

        const propertyContext = {
          properties: properties
        };

        const aiResponse = await generateResponse(message, propertyContext);
        
        setChatMessages(prev => [...prev, {
          text: aiResponse,
          sender: 'ai',
          timestamp: new Date()
        }]);
      } catch (error) {
        console.error('Error getting AI response:', error);
        setChatMessages(prev => [...prev, {
          text: 'I apologize, but I encountered an error. Please try again.',
          sender: 'ai',
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderMessageContent = (text) => {
    return (
      <Box sx={{ 
        width: '100%',
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          margin: '16px 0',
          fontSize: '1rem',
          backgroundColor: '#ffffff',
        },
        '& th': {
          border: '1px solid rgba(224, 224, 224, 1)',
          padding: '12px 16px',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          fontWeight: 'bold',
          textAlign: 'left',
          whiteSpace: 'nowrap',
        },
        '& td': {
          border: '1px solid rgba(224, 224, 224, 1)',
          padding: '12px 16px',
          textAlign: 'left',
          whiteSpace: 'nowrap',
        },
        '& p': { 
          margin: '12px 0', 
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        },
        '& ul, & ol': { 
          margin: '12px 0', 
          paddingLeft: '24px' 
        },
        '& pre': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          padding: '12px',
          borderRadius: '4px',
          overflowX: 'auto',
          margin: '12px 0',
        },
        '& code': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          padding: '3px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontFamily: 'monospace',
        },
        overflowX: 'auto',
        overflowY: 'hidden'
      }}>
        <ReactMarkdown components={{
          table: ({node, ...props}) => (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table {...props} style={{ minWidth: 'max-content' }} />
            </div>
          ),
          th: ({node, ...props}) => (
            <th {...props} style={{ 
              border: '1px solid rgba(224, 224, 224, 1)',
              padding: '12px 16px',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              fontWeight: 'bold',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            }} />
          ),
          td: ({node, ...props}) => (
            <td {...props} style={{ 
              border: '1px solid rgba(224, 224, 224, 1)',
              padding: '12px 16px',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            }} />
          ),
          p: ({node, children}) => (
            <Typography 
              variant="body1" 
              component="p" 
              sx={{ 
                my: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {children}
            </Typography>
          ),
          code: ({node, inline, children}) => (
            inline ? 
              <code style={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                padding: '3px 6px',
                borderRadius: '4px',
                fontSize: '0.9em',
                fontFamily: 'monospace',
              }}>{children}</code> :
              <pre style={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                padding: '12px',
                borderRadius: '4px',
                overflowX: 'auto',
                margin: '12px 0',
                fontFamily: 'monospace',
              }}><code>{children}</code></pre>
          )
        }}>
          {text}
        </ReactMarkdown>
      </Box>
    );
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        sx={sx}
      >
        <IconComponent />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '80vh',
            maxHeight: '700px'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: '#ffffff',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202123' }}>Portfolio AI Assistant</Typography>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
            bgcolor: '#ffffff',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            {chatMessages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '800px',
                    width: '100%',
                    mx: 'auto',
                    display: 'flex',
                    gap: 3,
                    flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: msg.sender === 'user' ? '#5436DA' : '#013F2E',
                      width: 30,
                      height: 30,
                    }}
                  >
                    {msg.sender === 'user' ? <PersonIcon /> : <AiIcon />}
                  </Avatar>
                  <Box sx={{ 
                    flex: 1,
                    textAlign: msg.sender === 'user' ? 'right' : 'left',
                  }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        bgcolor: msg.sender === 'user' ? '#F7F7F8' : '#F7F7F8',
                        borderRadius: '20px',
                        px: 3,
                        py: 2,
                        maxWidth: '90%',
                        '& p': {
                          m: 0,
                          color: '#000000',
                        }
                      }}
                    >
                      {renderMessageContent(msg.text)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
            {chatMessages.length >= 5 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 4,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  onClick={() => setShowMeetingScheduler(true)}
                  sx={{
                    borderRadius: 10,
                    px: 4,
                    py: 1,
                    borderColor: '#11A37F',
                    color: '#11A37F',
                    '&:hover': {
                      borderColor: '#0D8C6C',
                      bgcolor: 'rgba(17, 163, 127, 0.04)',
                    },
                  }}
                >
                  Schedule a Meeting with Account Manager
                </Button>
              </Box>
            )}
            {isLoading && (
              <Box 
                sx={{ 
                  p: 4, 
                  bgcolor: '#f7f7f8',
                  borderBottom: '1px solid',
                  borderColor: 'rgba(0,0,0,0.1)',
                }}
              >
                <Box 
                  sx={{
                    maxWidth: '800px',
                    width: '100%',
                    mx: 'auto',
                    display: 'flex',
                    gap: 3,
                  }}
                >
                  <Avatar sx={{ bgcolor: '#11A37F', width: 30, height: 30 }}>
                    <AiIcon />
                  </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#11A37F' }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3,
          // borderTop: '1px solid',
          borderColor: 'rgba(0,0,0,0.1)',
          bgcolor: '#ffffff',
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            maxWidth: '800px',
            position: 'relative',
          }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant="outlined"
              placeholder="Ask me anything about your properties..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9f9f9',
                  borderRadius: 10,
                  pr: 6,
                  '& fieldset': {
                    borderColor: 'rgba(0,0,0,0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0,0,0,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#013F2E',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!message.trim() || isLoading}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: message.trim() ? '#013F2E' : 'transparent',
                color: message.trim() ? '#ffffff' : 'rgba(0,0,0,0.5)',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: message.trim() ? '#0D8C6C' : 'transparent',
                },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  color: 'rgba(0,0,0,0.3)',
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} sx={{ color: '#11A37F' }} /> : <SendIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>
      <MeetingScheduler
        open={showMeetingScheduler}
        onClose={() => setShowMeetingScheduler(false)}
      />
    </>
  );
}

export default ChatButton;
